import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { IconEnum } from 'components/common/Icon';
import Icon from 'components/common/Icon';

const Container = styled.div<{ isDarkMode: boolean; isFocused: boolean; isFullPage?: boolean }>`
    display: flex;
    align-items: center;
    background: var(--color-alt-second);
    padding: 6px 6px 6px 15px;
    border-radius: 20px;
    border: 1px solid var(--color-last-message);
    transition: border-color 0.2s ease;
    z-index: 300;

    ${(props) => props.isDarkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}

    ${(props) => props.isFocused && `
        border-color: var(--color-last-message);
    `}
`;

const TextArea = styled.textarea<{ isFullPage?: boolean }>`
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 20px;
    width: 100%;
    flex: 1;
    background: transparent;
    border: none;
    border-radius: 20px;
    color: var(--color-primary);
    outline: none;
    resize: none;
    overflow-y: auto;
    min-height: ${(props) => props.isFullPage ? '50px' : '20px'};
    max-height: ${(props) => props.isFullPage ? '80px' : '100px'};
    width: ${(props) => props.isFullPage ? '100%' : 'auto'};
    padding: 0 15px 0 15px;
    box-sizing: border-box;

    &::placeholder {
        color: var(--color-primary);
        opacity: 0.7;
        font-weight: var(--font-weight);
        font-size: var(--font-size-body-2);
        line-height: 20px;
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const SendButton = styled.div<{ isDisabled?: boolean }>`
    background: var(--color-alt);
    width: 48px;
    min-width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    cursor: ${(props) => props.isDisabled ? 'not-allowed' : 'pointer'};
    opacity: ${(props) => props.isDisabled ? '0.5' : '1'};
    margin-left: 8px;

    img {
        filter: brightness(0) invert(1);
        width: 22px;
        height: 22px;
    }
`;

interface ExpandableTextInputProps {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    placeholder?: string;
    disabled?: boolean;
    isDarkMode: boolean;
    maxRows?: number;
    isFullPage?: boolean;
}

const ExpandableTextInput: FC<ExpandableTextInputProps> = ({
    value,
    onChange,
    onSend,
    placeholder = 'Napisz wiadomość...',
    disabled = false,
    isDarkMode,
    isFullPage,
    maxRows = 4
}) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';

            const lineHeight = 20;
            const maxHeight = lineHeight * maxRows;
            const scrollHeight = textAreaRef.current.scrollHeight;
            const newHeight = Math.min(scrollHeight, maxHeight);

            textAreaRef.current.style.height = `${newHeight}px`;
        }
    }, [value, maxRows]);

    useEffect(() => {
        if (!disabled && textAreaRef.current) {
            textAreaRef.current.focus();
        }
    }, [disabled]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (value.trim() && !disabled) {
                onSend();
            }
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.focus();
        }
    }, []);

    return (
        <Container isDarkMode={isDarkMode} isFocused={isFocused} isFullPage={isFullPage}>
            <TextArea
                ref={textAreaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={1}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isFullPage={isFullPage}
            />
            <SendButton onClick={disabled ? undefined : onSend} isDisabled={disabled}>
                <Icon icon={IconEnum.SEND} />
            </SendButton>
        </Container>
    );
};

export default ExpandableTextInput;
